import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import type { PhoneAttestationChannel } from '@/types';
import { toDateTimeLocalValue } from './signatureLabels';

const CHANNELS: PhoneAttestationChannel[] = [
  'phone',
  'in_person',
  'email',
  'sms',
  'whatsapp',
  'other',
];

const CHANNEL_LABEL: Record<PhoneAttestationChannel, string> = {
  phone: 'signature.channelPhone',
  in_person: 'signature.channelInPerson',
  email: 'signature.channelEmail',
  sms: 'signature.channelSms',
  whatsapp: 'signature.channelWhatsapp',
  other: 'signature.channelOther',
};

export interface PhoneAttestationDialogProps {
  open: boolean;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (input: {
    customerName: string;
    channel: PhoneAttestationChannel;
    receivedAt: string;
    statement: string;
  }) => Promise<void>;
}

export function PhoneAttestationDialog({
  open,
  submitting,
  onClose,
  onSubmit,
}: PhoneAttestationDialogProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const [customerName, setCustomerName] = useState('');
  const [channel, setChannel] = useState<PhoneAttestationChannel>('phone');
  const [receivedAt, setReceivedAt] = useState(() => toDateTimeLocalValue(new Date()));
  const [statement, setStatement] = useState('');
  const [nameError, setNameError] = useState(false);
  const [statementError, setStatementError] = useState(false);
  const [timeError, setTimeError] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  const reset = () => {
    setCustomerName('');
    setChannel('phone');
    setReceivedAt(toDateTimeLocalValue(new Date()));
    setStatement('');
    setNameError(false);
    setStatementError(false);
    setTimeError(false);
    setSubmitError(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    const name = customerName.trim();
    const text = statement.trim();
    setNameError(name.length === 0);
    setStatementError(text.length === 0);
    const parsed = new Date(receivedAt);
    const invalidTime = Number.isNaN(parsed.getTime());
    setTimeError(invalidTime);
    if (name.length === 0 || text.length === 0 || invalidTime) return;
    const iso = parsed.toISOString();
    setSubmitError(false);
    try {
      await onSubmit({
        customerName: name,
        channel,
        receivedAt: iso,
        statement: text,
      });
      reset();
    } catch {
      setSubmitError(true);
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={handleClose}
      mobileTitle={t('signature.phoneTitle')}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t('signature.phoneTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {submitError && <Alert severity="error">{t('signature.saveFailed')}</Alert>}
          <TextField
            label={t('signature.phoneCustomerName')}
            value={customerName}
            onChange={(event) => {
              setCustomerName(event.target.value);
              setNameError(false);
            }}
            error={nameError}
            helperText={nameError ? t('signature.phoneCustomerNameRequired') : undefined}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel id="phone-channel-label">{t('signature.phoneChannel')}</InputLabel>
            <Select
              labelId="phone-channel-label"
              label={t('signature.phoneChannel')}
              value={channel}
              onChange={(event) => setChannel(event.target.value as PhoneAttestationChannel)}
            >
              {CHANNELS.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(CHANNEL_LABEL[value])}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={t('signature.phoneTime')}
            type="datetime-local"
            value={receivedAt}
            onChange={(event) => {
              setReceivedAt(event.target.value);
              setTimeError(false);
            }}
            error={timeError}
            helperText={timeError ? t('signature.phoneTimeRequired') : undefined}
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label={t('signature.phoneStatement')}
            value={statement}
            onChange={(event) => {
              setStatement(event.target.value);
              setStatementError(false);
            }}
            error={statementError}
            helperText={statementError ? t('signature.phoneStatementRequired') : undefined}
            fullWidth
            multiline
            minRows={3}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {tc('cancel')}
        </Button>
        <Button variant="contained" onClick={() => void handleSubmit()} disabled={submitting}>
          {t('signature.phoneSubmit')}
        </Button>
      </DialogActions>
    </ResponsiveDialog>
  );
}
